import React, { ReactNode, Fragment, useEffect } from 'react'
import { Transition } from '@headlessui/react'

export type SlideOverPropTypes = {
  isOpen?: boolean
  onOpen?: () => void
  onClose?: () => void
  position?: 'left' | 'right' | 'top' | 'bottom'
  backdropColor?: string
  backdropOpacity?: string
  transitionDuration?: string
  transparentBackdrop?: boolean
  children?: ReactNode
}

const transitionVariants = {
  right: {
    size: 'h-full w-auto',
    fixedPosition: 'inset-y-0 right-0',
    hidePosition: 'translate-x-full',
    showPosition: 'translate-x-0'
  },
  left: {
    size: 'h-full w-auto',
    fixedPosition: 'inset-y-0 left-0',
    hidePosition: '-translate-x-full',
    showPosition: 'translate-x-0'
  },
  top: {
    size: 'h-auto w-full',
    fixedPosition: 'inset-x-0 top-0',
    hidePosition: '-translate-y-full',
    showPosition: 'translate-y-0'
  },
  bottom: {
    size: 'h-auto w-full',
    fixedPosition: 'inset-x-0 bottom-0',
    hidePosition: 'translate-y-full',
    showPosition: 'translate-y-0'
  }
}

const SlideOver = ({
  isOpen = false,
  onOpen,
  onClose,
  position = 'right',
  backdropColor = 'bg-black',
  backdropOpacity = 'bg-opacity-50',
  transitionDuration = 'duration-500',
  transparentBackdrop,
  ...props
}: SlideOverPropTypes) => {
  const drawerVariant = transitionVariants[position]
  const backdrop = backdropColor + ' ' + backdropOpacity

  const children =
    typeof props.children === 'function'
      ? props.children({ onClose })
      : props.children

  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose && onClose()
      }
    }

    window.addEventListener('keydown', close)

    return () => {
      window.removeEventListener('keydown', close)
    }
  }, [])

  useEffect(() => {
    const originalOverflowStyle = window.getComputedStyle(
      document.body
    ).overflow

    if (isOpen) {
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.body.style.overflow = originalOverflowStyle
    }
  }, [isOpen])

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <div className='fixed z-[1000] h-screen w-screen overflow-hidden'>
        <Transition.Child
          as={Fragment}
          enter={`transition opacity ease-in-out ${transitionDuration}`}
          enterFrom={'opacity-0'}
          enterTo={'opacity-100'}
          leave={`transition opacity ease-in-out ${transitionDuration}`}
          leaveFrom={'opacity-100'}
          leaveTo={'opacity-0'}
        >
          <div
            onClick={onClose && (() => onClose())}
            className={`fixed inset-0 h-screen w-screen ${
              transparentBackdrop ? 'bg-transparent' : backdrop
            }`}
          />
        </Transition.Child>
        <div className={`fixed ${drawerVariant.fixedPosition} flex max-h-full`}>
          <Transition.Child
            as={Fragment}
            enter={`transition transform ease-in-out ${transitionDuration}`}
            enterFrom={drawerVariant.hidePosition}
            enterTo={drawerVariant.showPosition}
            leave={`transition transform ease-in-out ${transitionDuration}`}
            leaveFrom={drawerVariant.showPosition}
            leaveTo={drawerVariant.hidePosition}
            afterEnter={onOpen && (() => onOpen())}
          >
            <div className={drawerVariant.size}>{children}</div>
          </Transition.Child>
        </div>
      </div>
    </Transition.Root>
  )
}

export default SlideOver
